/**
 * StreamCoordinator
 *
 * Renderer-side authority over the host's view of Playjector's stream world. Replaces the former
 * electron `StreamRegistry` + replicated `streamState` store: the electron process is now a pipe
 * that forwards raw `stream.*` replies here, and this coordinator owns the state in-memory.
 *
 * Responsibilities:
 * - Send `stream.*` commands (enumerate / list / create / remove / modify) through IpcService.
 * - Consume Playjector `stream.*` replies via `handleReply` and keep the registry in sync.
 * - Expose read getters + a `streamStateChanged` semantic event for consumers (DisplayOutputManager,
 *   `ipcInterface.changeDisplay`, UI).
 * - Drive the preview-camera -> base frame-sink mapping and the initial base-stream display retarget.
 *
 * Initialized in prime mode too (independent of USE_MESSAGE_ROUTER), so stream tracking works for
 * the legacy prime host as well as pavilion/beacon.
 *
 * Pattern: Coordinator -> Manager (StreamRegistryManager).
 */

import type { HostStreamArchState, RegistryId, StreamComposition, StreamFrameOutputEntry, StreamId, PlaycastMessage, PlaycastMessageTarget } from '@playcastdotio/Messaging';
import { CoordinatorEventImpl, type CoordinatorEvent } from '../_types/events';
import type { IIpcService } from '../../services/ipc/IIpcService';
import { USE_STREAM_ARCHITECTURE } from '../../config/featureFlags';
import { StreamRegistryManager } from './managers/StreamRegistryManager';
import { BASE_STREAM_CAMERA } from '../media/cameraNames';

export interface StreamCoordinatorDeps {
  ipcService: IIpcService;
  /**
   * Subscribe to preview-camera changes (renderer `hostRuleState.videoPreviewCamera`). Returns an
   * unsubscribe function. When the preview camera changes the coordinator re-points the base
   * stream's frame sink at the matching ShadowCam. Optional so tests can omit it.
   */
  subscribePreviewCamera?: (onCamera: (camera: string) => void) => () => void;
  /**
   * Read the currently selected host display id (renderer `displayState.info.selectedDisplayId`).
   * Used for the one-time post-priming retarget so the base stream lands on the right monitor at
   * startup. Optional so tests can omit it.
   */
  getSelectedDisplayId?: () => number | undefined;
  /**
   * Name of the desktop-hosting camera the peers consume (Playjector's BASE_STREAM_CAMERA). It is
   * protected from removal when a preview swap re-points the base frame sink, so hosted desktop
   * video never loses frames. Optional; defaults to `BASE_STREAM_CAMERA` from
   * coordinators/media/cameraNames (matches the C++ default).
   */
  desktopHostingCamera?: string;
}

// The desktop-hosting camera the peers consume by default. Alias of the shared
// BASE_STREAM_CAMERA (mirrors C++ Constants::BASE_STREAM_CAMERA and the
// electron/host `config.PREVIEW_SHADOWCAM`).
const DEFAULT_DESKTOP_HOSTING_CAMERA = BASE_STREAM_CAMERA;

export class StreamCoordinator {
  private readonly registry: StreamRegistryManager;
  private readonly deps: StreamCoordinatorDeps;
  private readonly desktopHostingCamera: string;
  private initialized = false;
  private lastPreviewCamera = '';
  // A preview camera requested before its sink appeared in the catalog. Retried on the next
  // registry change (after prime's replies land) so the base stream eventually points at it.
  private pendingPreviewCamera = '';
  // Peer-activated ("pinned") frame sinks. Added additively to the base stream and NEVER removed by
  // a preview swap, so activating a peer camera (e.g. ShadowCam01 for mobile) is not paused when the
  // host operator later switches the preview camera.
  private activatedSinks = new Set<RegistryId>();
  // The frame sink the base stream currently shows as the "preview" camera (swappable). Only this
  // sink is removed on a preview swap - and only when it is neither pinned nor the hosting camera.
  private previewSinkId: RegistryId | undefined;
  // Single-slot pending display retarget for a cold catalog. Latest request wins: a newer pick
  // overwrites this before the older one can apply, so a stale display id can never commit.
  private pendingDisplayRetarget: { displayId: number; onApplied: () => void } | null = null;
  private initialDisplayRetargeted = false;
  private unsubscribers: Array<() => void> = [];

  public readonly events: { streamStateChanged: CoordinatorEvent<HostStreamArchState> } = {
    streamStateChanged: new CoordinatorEventImpl<HostStreamArchState>(),
  };

  constructor(deps: StreamCoordinatorDeps) {
    this.deps = deps;
    this.desktopHostingCamera = deps.desktopHostingCamera ?? DEFAULT_DESKTOP_HOSTING_CAMERA;
    this.registry = new StreamRegistryManager(
      (message) => deps.ipcService.send(message),
      (state) => this.onRegistryChanged(state),
    );
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    if (this.deps.subscribePreviewCamera) {
      this.unsubscribers.push(
        this.deps.subscribePreviewCamera((camera) => this.onPreviewCameraChanged(camera)),
      );
    }

    console.log('[StreamCoordinator] Initialized (enabled=%s)', USE_STREAM_ARCHITECTURE);
  }

  destroy(): void {
    this.unsubscribers.forEach((fn) => {
      try {
        fn();
      } catch (err) {
        console.warn('[StreamCoordinator] unsubscribe failed', err);
      }
    });
    this.unsubscribers = [];
    this.initialized = false;
  }

  // ========================================
  // Public API - Priming / enumeration
  // ========================================

  /** Seed the catalogs (frameOutputs + the unified streamSinks list) from Playjector. */
  enumerateAll(): void {
    this.registry.enumerateAll();
  }

  /** Seed the stream table (and base-stream id) from Playjector. */
  list(): void {
    this.registry.list();
  }

  /** Prime both catalogs and the stream table. Called on Playjector (re)initialization. */
  prime(): void {
    this.registry.enumerateAll();
    this.registry.list();
  }

  // ========================================
  // Public API - Lifecycle / composition commands
  // ========================================

  createStream(composition: StreamComposition): void {
    this.registry.createStream(composition);
  }

  removeStream(streamId: StreamId): void {
    this.registry.removeStream(streamId);
  }

  setFrameOutput(streamId: StreamId, frameOutput: RegistryId): void {
    this.registry.setFrameOutput(streamId, frameOutput);
  }

  addSink(streamId: StreamId, sink: RegistryId): void {
    this.registry.addSink(streamId, sink);
  }

  removeSink(streamId: StreamId, sink: RegistryId): void {
    this.registry.removeSink(streamId, sink);
  }

  // Per-type mute for a sink: gate one data type of a multi-type sink (e.g. an A/V camera's audio)
  // without changing what it can carry. For single-type sinks, use add/removeSink instead.
  setSinkTypeEnabled(registryId: RegistryId, type: number, enabled: boolean): void {
    this.registry.setSinkTypeEnabled(registryId, type, enabled);
  }

  setBaseFrameOutput(frameOutput: RegistryId): boolean {
    return this.registry.setBaseFrameOutput(frameOutput);
  }

  /**
   * Point the base stream's PREVIEW camera at `frameSink` (Ctrl+F12 / dev preview select). Removes
   * the previous preview sink only when it is safe: not the same sink, not the desktop-hosting camera
   * (peers consume it), and not a pinned peer sink (activateFrameSink). This keeps "one preview camera
   * at a time" without ever detaching the hosting camera or a live peer camera.
   */
  setBaseFrameSink(frameSink: RegistryId): boolean {
    const hostingSink = this.registry.resolveFrameSinkByName(this.desktopHostingCamera);

    let sinkToRemove: RegistryId | undefined;
    if (
      this.previewSinkId !== undefined &&
      this.previewSinkId !== frameSink &&
      this.previewSinkId !== hostingSink &&
      !this.activatedSinks.has(this.previewSinkId)
    ) {
      sinkToRemove = this.previewSinkId;
    }

    const ok = this.registry.setBaseFrameSink(frameSink, sinkToRemove);
    if (ok) this.previewSinkId = frameSink;
    return ok;
  }

  /**
   * Activate a frame sink for a peer additively: add it to the base stream and PIN it, so a later
   * preview swap never removes it (which would pause a live peer camera via C++
   * OnStreamFrameBinding(false)). Idempotent - re-activating an already-pinned sink just re-adds
   * (deduped by the stream). Returns false only when no base stream is known yet.
   */
  activateFrameSink(frameSink: RegistryId): boolean {
    const id = this.registry.baseStreamId();
    if (!id) return false;
    this.activatedSinks.add(frameSink);
    this.registry.addSink(id, frameSink);
    return true;
  }

  /**
   * Resolve a frame-capable sink by name, waiting for it to appear in the catalog. `prime()` fetches
   * the catalog asynchronously, so a synchronous read right after priming misses on a cold start;
   * this primes and then re-checks on every `streamStateChanged` until the sink resolves or the
   * timeout elapses (returns undefined on timeout).
   */
  waitForFrameSink(name: string, timeoutMs = 8_000): Promise<RegistryId | undefined> {
    const existing = this.registry.resolveFrameSinkByName(name);
    if (existing !== undefined) return Promise.resolve(existing);

    return new Promise<RegistryId | undefined>((resolve) => {
      let settled = false;
      const finish = (value: RegistryId | undefined) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        unsubscribe();
        resolve(value);
      };

      const unsubscribe = this.events.streamStateChanged.addListener(() => {
        const id = this.registry.resolveFrameSinkByName(name);
        if (id !== undefined) finish(id);
      });

      const timer = setTimeout(() => finish(undefined), timeoutMs);

      // Re-fetch the catalog and re-check now in case the sink appeared between the initial read and
      // the subscription being wired up.
      this.prime();
      const id = this.registry.resolveFrameSinkByName(name);
      if (id !== undefined) finish(id);
    });
  }

  // ========================================
  // Public API - Getters / resolvers
  // ========================================

  getState(): HostStreamArchState {
    return this.registry.getState();
  }

  getBaseStreamId(): StreamId {
    return this.registry.baseStreamId();
  }

  getFrameOutputs(): StreamFrameOutputEntry[] {
    return this.registry.getFrameOutputs();
  }

  resolveFrameOutputForDisplay(displayId: number): RegistryId | undefined {
    return this.registry.resolveFrameOutputForDisplay(displayId);
  }

  resolveFrameSinkByName(name: string): RegistryId | undefined {
    return this.registry.resolveFrameSinkByName(name);
  }

  // ========================================
  // Public API - Inbound reply entry
  // ========================================

  /** Consume a raw `stream.*` reply forwarded from electron. Returns true when consumed. */
  handleReply(message: PlaycastMessage<PlaycastMessageTarget>): boolean {
    return this.registry.handleReply(message);
  }

  // ========================================
  // Public API - Display retarget
  // ========================================

  /**
   * Retarget the base stream's frame output to the chosen display. Resolves the display id to a
   * frame-output RegistryId (a grabber's sourceId IS its monitor_index, and the picker keys on that
   * same sourceId). Returns false when the display or base stream is not yet known; the caller may
   * re-prime and retry. `setPicking` / follow-mouse is handled inside Playjector and is unchanged.
   */
  retargetBaseStreamToDisplay(displayId: number): boolean {
    const frameOutput = this.registry.resolveFrameOutputForDisplay(displayId);
    if (frameOutput === undefined) {
      console.warn('[StreamCoordinator] frame output for display not found', displayId);
      return false;
    }
    if (!this.registry.baseStreamId()) {
      console.warn('[StreamCoordinator] base stream id not known yet');
      return false;
    }
    return this.registry.setBaseFrameOutput(frameOutput);
  }

  // ========================================
  // Internal
  // ========================================

  /**
   * Retarget the base stream to `displayId`, committing the UI (`onApplied`) only once the retarget
   * lands. Latest-wins: this supersedes any prior pending request before trying, so a rapid re-pick
   * can never be clobbered by an older cold-catalog retry. On a not-yet-ready catalog it primes and
   * defers to `tryApplyPendingDisplayRetarget` (fired on the next `streamStateChanged`).
   */
  requestDisplayRetarget(displayId: number, onApplied: () => void): void {
    // Supersede any older pending request so only the newest pick can ever apply/commit.
    this.pendingDisplayRetarget = null;

    if (this.retargetBaseStreamToDisplay(displayId)) {
      onApplied();
      return;
    }

    this.pendingDisplayRetarget = { displayId, onApplied };
    this.prime();
  }

  private onRegistryChanged(state: HostStreamArchState): void {
    this.events.streamStateChanged.emit(state);
    this.tryInitialDisplayRetarget(state);
    this.tryApplyPendingPreviewCamera();
    this.tryApplyPendingDisplayRetarget();
  }

  /**
   * Apply a display retarget that was requested before the catalog was ready. Runs on every registry
   * change (after prime's replies land). Commits the UI via the stored callback and clears the slot
   * on success; a superseding request will have already replaced the slot, so only the latest pick
   * commits.
   */
  private tryApplyPendingDisplayRetarget(): void {
    const pending = this.pendingDisplayRetarget;
    if (!pending) return;

    if (this.retargetBaseStreamToDisplay(pending.displayId)) {
      this.pendingDisplayRetarget = null;
      pending.onApplied();
    }
  }

  /**
   * Resolve a preview-camera change that was requested before its sink was in the catalog. Runs on
   * every registry change (i.e. after prime's enumerate/list replies land), so the base stream
   * eventually points at the requested camera without waiting for the user to switch again.
   */
  private tryApplyPendingPreviewCamera(): void {
    if (!this.pendingPreviewCamera) return;

    const frameSinkId = this.registry.resolveFrameSinkByName(this.pendingPreviewCamera);
    if (frameSinkId === undefined) return;

    this.setBaseFrameSink(frameSinkId);
    this.lastPreviewCamera = this.pendingPreviewCamera;
    this.pendingPreviewCamera = '';
  }

  /**
   * Once priming establishes the base stream (and the frame-output catalog), retarget it to the
   * currently-selected host display so it lands on the right monitor at startup. Runs at most once
   * per priming cycle; reset when the base stream is torn down. Playjector's base stream defaults to
   * the primary monitor, so a missing/unresolvable selection simply leaves it there.
   */
  private tryInitialDisplayRetarget(state: HostStreamArchState): void {
    if (!state.baseStreamId) {
      this.initialDisplayRetargeted = false;
      return;
    }
    if (this.initialDisplayRetargeted) return;
    if (!this.deps.getSelectedDisplayId) return;

    const displayId = this.deps.getSelectedDisplayId();
    if (displayId === undefined || displayId < 0) return;

    if (this.retargetBaseStreamToDisplay(displayId)) {
      this.initialDisplayRetargeted = true;
    }
  }

  private onPreviewCameraChanged(camera: string): void {
    if (!USE_STREAM_ARCHITECTURE) return;
    if (!camera) return;
    // Preserve the legacy "already active -> no-op" early-exit: only re-point when the preview
    // camera actually changed. lastPreviewCamera is only committed once the sink is actually applied
    // (below / in tryApplyPendingPreviewCamera), so a change requested before the sink exists is not
    // silently swallowed.
    if (camera === this.lastPreviewCamera) return;

    const frameSinkId = this.registry.resolveFrameSinkByName(camera);
    if (frameSinkId !== undefined) {
      // setBaseFrameSink protects the hosting camera and any pinned peer sinks from removal.
      this.setBaseFrameSink(frameSinkId);
      this.lastPreviewCamera = camera;
      this.pendingPreviewCamera = '';
    } else {
      // The sink is not in the catalog yet. Do NOT commit lastPreviewCamera; record it as pending and
      // re-prime, so tryApplyPendingPreviewCamera re-points the base stream once the sink shows up in
      // a subsequent reply (Cursor #2 — otherwise the swap is lost until the user switches again).
      this.pendingPreviewCamera = camera;
      this.prime();
    }
  }
}
